import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { AdminSupportCategory } from "view/admin/utils/admin.types";
import { adminSupportCategorySchema, AdminSupportCategorySchemaInput, AdminSupportCategorySchemaOutput } from "view/admin/utils/admin.schema";

import { SaveAdminFaqCategoryDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface SupportCategoryCreateEditDialogProps {
	open: boolean;
	onClose: () => void;
	category: AdminSupportCategory | null;
	refetch: () => void;
}

const SupportCategoryCreateEditDialog = ({ open, onClose, category, refetch }: SupportCategoryCreateEditDialogProps) => {

	const { handleSubmit, control, reset } = useForm<AdminSupportCategorySchemaInput, unknown, AdminSupportCategorySchemaOutput>({
		resolver: zodResolver(adminSupportCategorySchema),
		defaultValues: {
			id: null,
			name: "",
			sequenceNumber: null,
		},
	});

	const [ saveAdminFaqCategory, { loading } ] = useMutation(SaveAdminFaqCategoryDocument);

	const save = (data: AdminSupportCategorySchemaOutput) => {
		saveAdminFaqCategory({
			variables: {
				input: {
					id: data.id,
					name: data.name,
					sequenceNumber: data.sequenceNumber,
				},
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && category) {
			reset({
				id: category.id ?? null,
				name: category.name ?? "",
				sequenceNumber: category.sequenceNumber ?? null,
			});
		} else if (open) {
			reset({
				id: null,
				name: "",
				sequenceNumber: null,
			});
		}
	}, [ open, category ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "ADMIN_FAQ_CATEGORY_TITLE" })}
			form="adminFaqCategoryForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="adminFaqCategoryForm">
				<FormGroup
					label={intl.formatMessage({ id: "NAME" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="name"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default SupportCategoryCreateEditDialog;
